import { HorizontalTagInput, TagOption } from '@/components/core'
import { PolarityEmoji } from '@/pages/Mentions/components/Polarity/Polarity.component'
import { PreviewOpen } from '@icon-park/react'
import { ChangeEvent } from 'react'
import { BrandRules, BrandTerms, DataSourceType, PolarityType } from '../../types'
import { CONSTANTS, getCurrentRules, getPolarityIcon, getRulesCount } from '../../utils/index'
import {
  HealthCheckStatus,
  PolarityContent,
  PolarityHeader,
  PolarityStatusSection,
  PolarityTermsSection,
  PolarityTitleSection,
  RulesCountSpan,
  RulesSection,
  RulesTextArea,
  RulesTitle,
  RulesTitleWithStatus,
  RulesValidationContainer,
  SmallPolarityEmoji,
  TermSection,
  TermsGrid,
  ViewRulesButton,
} from './PolarityForm.styles'

interface PolarityFormProps {
  title: string
  specificField: string
  expanded: boolean
  polarity: PolarityType
  activeTab: DataSourceType
  rules: BrandRules
  termsState: BrandTerms
  onRulesChange: (field: string, value: string) => void
  onTermsChange: (updates: Partial<BrandTerms>) => void
  onPreviewClick: () => void
  tagValidator: (newTags: TagOption[], existingTags: TagOption[]) => TagOption[]
  required?: boolean
  showRequiredLabel?: boolean
}

export function PolarityForm({
  title,
  specificField,
  expanded,
  polarity,
  activeTab,
  rules,
  termsState,
  onRulesChange,
  onTermsChange,
  onPreviewClick,
  tagValidator,
  required = true,
  showRequiredLabel = true,
}: PolarityFormProps): JSX.Element {
  function renderPolarityHeader(): JSX.Element {
    const currentRules = getCurrentRules(rules, activeTab)
    const specificRulesCount = getRulesCount(currentRules[specificField as keyof typeof currentRules])

    return (
      <PolarityHeader $expanded={expanded}>
        <PolarityTitleSection>
          <SmallPolarityEmoji>
            <PolarityEmoji value={getPolarityIcon(title)} />
          </SmallPolarityEmoji>
          <span>{title}</span>
        </PolarityTitleSection>
        <div>
          {showRequiredLabel && (
            <span
              style={{
                fontSize: '12px',
                color: required ? '#e53935' : '#6b7280',
                fontWeight: 600,
                marginRight: '12px',
              }}>
              {required ? '*obrigatório' : '(opcional)'}
            </span>
          )}
          {expanded && (
            <PolarityStatusSection>
              <ViewRulesButton onClick={onPreviewClick}>
                <PreviewOpen size={14} />
                Ver premissas ({specificRulesCount})
              </ViewRulesButton>
            </PolarityStatusSection>
          )}
        </div>
      </PolarityHeader>
    )
  }

  function renderPolarityTermsSection(): JSX.Element {
    const isActiveTabSocial = activeTab === 'social'

    if (isActiveTabSocial) {
      const publishersTags =
        polarity === 'POSITIVO' ? termsState.socialPositivePublishersTags
        : polarity === 'NEGATIVO' ? termsState.socialNegativePublishersTags
        : polarity === 'FAVORAVEL' ? termsState.socialFavorablePublishersTags
        : termsState.socialUnfavorablePublishersTags

      const proprietaryTags =
        polarity === 'POSITIVO' ? termsState.socialPositiveProprietaryTags
        : polarity === 'NEGATIVO' ? termsState.socialNegativeProprietaryTags
        : polarity === 'FAVORAVEL' ? termsState.socialFavorableProprietaryTags
        : termsState.socialUnfavorableProprietaryTags

      return (
        <PolarityTermsSection>
          <TermsGrid>
            <TermSection>
              <HorizontalTagInput
                label="Publicadores"
                placeholder="Digite publicadores separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={(tags) =>
                  onTermsChange({
                    [`social${
                      polarity === 'POSITIVO' ? 'Positive'
                      : polarity === 'NEGATIVO' ? 'Negative'
                      : polarity === 'FAVORAVEL' ? 'Favorable'
                      : 'Unfavorable'
                    }PublishersTags`]: tags,
                  })
                }
                preSelectedTags={publishersTags}
                validationConfig={{
                  maxCharacters: 200,
                  allowDuplicates: false,
                  customValidator: tagValidator,
                }}
                showClearButton={true}
              />
            </TermSection>
            <TermSection>
              <HorizontalTagInput
                label="Termos Proprietários"
                placeholder="Digite termos separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={(tags) =>
                  onTermsChange({
                    [`social${
                      polarity === 'POSITIVO' ? 'Positive'
                      : polarity === 'NEGATIVO' ? 'Negative'
                      : polarity === 'FAVORAVEL' ? 'Favorable'
                      : 'Unfavorable'
                    }ProprietaryTags`]: tags,
                  })
                }
                preSelectedTags={proprietaryTags}
                validationConfig={{
                  maxCharacters: 200,
                  allowDuplicates: false,
                  customValidator: tagValidator,
                }}
                showClearButton={true}
              />
            </TermSection>
          </TermsGrid>
        </PolarityTermsSection>
      )
    } else {
      const channelsTags =
        polarity === 'POSITIVO' ? termsState.pressPositiveChannelsTags
        : polarity === 'NEGATIVO' ? termsState.pressNegativeChannelsTags
        : polarity === 'FAVORAVEL' ? termsState.pressFavorableChannelsTags
        : termsState.pressUnfavorableChannelsTags

      const proprietaryTags =
        polarity === 'POSITIVO' ? termsState.pressPositiveProprietaryTags
        : polarity === 'NEGATIVO' ? termsState.pressNegativeProprietaryTags
        : polarity === 'FAVORAVEL' ? termsState.pressFavorableProprietaryTags
        : termsState.pressUnfavorableProprietaryTags

      return (
        <PolarityTermsSection>
          <TermsGrid>
            <TermSection>
              <HorizontalTagInput
                label="Veículos"
                placeholder="Digite veículos separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={(tags) =>
                  onTermsChange({
                    [`press${
                      polarity === 'POSITIVO' ? 'Positive'
                      : polarity === 'NEGATIVO' ? 'Negative'
                      : polarity === 'FAVORAVEL' ? 'Favorable'
                      : 'Unfavorable'
                    }ChannelsTags`]: tags,
                  })
                }
                preSelectedTags={channelsTags}
                validationConfig={{
                  maxCharacters: 200,
                  allowDuplicates: false,
                  customValidator: tagValidator,
                }}
                showClearButton={true}
              />
            </TermSection>
            <TermSection>
              <HorizontalTagInput
                label="Termos Proprietários"
                placeholder="Digite termos separados por ponto e vírgula (;)"
                maxCharacters={200}
                onChange={(tags) =>
                  onTermsChange({
                    [`press${
                      polarity === 'POSITIVO' ? 'Positive'
                      : polarity === 'NEGATIVO' ? 'Negative'
                      : polarity === 'FAVORAVEL' ? 'Favorable'
                      : 'Unfavorable'
                    }ProprietaryTags`]: tags,
                  })
                }
                preSelectedTags={proprietaryTags}
                validationConfig={{
                  maxCharacters: 200,
                  allowDuplicates: false,
                  customValidator: tagValidator,
                }}
                showClearButton={true}
              />
            </TermSection>
          </TermsGrid>
        </PolarityTermsSection>
      )
    }
  }

  if (!expanded) {
    return renderPolarityHeader()
  }

  const currentRules = getCurrentRules(rules, activeTab)
  const specificRulesCount = getRulesCount(currentRules[specificField as keyof typeof currentRules])
  const hasValidSpecificRules = specificRulesCount > 0 && specificRulesCount <= CONSTANTS.MAX_RULES_PER_POLARITY

  return (
    <PolarityContent>
      {renderPolarityHeader()}
      {renderPolarityTermsSection()}
      <RulesSection data-rules-specific>
        <RulesTitleWithStatus>
          <RulesTitle>Premissas *</RulesTitle>
          <RulesValidationContainer>
            <RulesCountSpan>{specificRulesCount}/15 premissas</RulesCountSpan>
            <HealthCheckStatus $status={hasValidSpecificRules} />
          </RulesValidationContainer>
        </RulesTitleWithStatus>
        <RulesTextArea
          placeholder="Digite as premissas separadas por ponto e vírgula (;)&#10;Exemplo: Premissa 1; Premissa 2; Premissa 3"
          value={currentRules[specificField as keyof typeof currentRules]}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onRulesChange(specificField, e.target.value)}
        />
      </RulesSection>
    </PolarityContent>
  )
}

export function NeutralPolarityForm({
  title,
  expanded,
  activeTab,
  rules,
  onRulesChange,
  required = true,
  showRequiredLabel = true,
  onPreviewClick,
}: {
  title: string
  expanded: boolean
  activeTab: DataSourceType
  rules: BrandRules
  onRulesChange: (field: string, value: string) => void
  required?: boolean
  showRequiredLabel?: boolean
  onPreviewClick?: () => void
}): JSX.Element {
  const currentRules = getCurrentRules(rules, activeTab)
  const rulesCount = getRulesCount(currentRules.neutralRules)
  const hasValidRules = rulesCount > 0 && rulesCount <= CONSTANTS.MAX_RULES_PER_POLARITY

  return (
    <PolarityContent>
      <PolarityHeader $expanded={expanded}>
        <PolarityTitleSection>
          <SmallPolarityEmoji>
            <PolarityEmoji value={getPolarityIcon('NEUTRO')} />
          </SmallPolarityEmoji>
          <span>{title}</span>
        </PolarityTitleSection>
        <div>
          {showRequiredLabel && (
            <span
              style={{
                fontSize: '12px',
                color: required ? '#e53935' : '#6b7280',
                fontWeight: 600,
                marginRight: '12px',
              }}>
              {required ? '*obrigatório' : '(opcional)'}
            </span>
          )}
          {expanded && onPreviewClick && (
            <PolarityStatusSection>
              <ViewRulesButton onClick={onPreviewClick}>
                <PreviewOpen size={14} />
                Ver premissas ({rulesCount})
              </ViewRulesButton>
            </PolarityStatusSection>
          )}
        </div>
      </PolarityHeader>
      <RulesSection data-rules-specific>
        <RulesTitleWithStatus>
          <RulesTitle>Premissas *</RulesTitle>
          <RulesValidationContainer>
            <RulesCountSpan>{rulesCount}/15 premissas</RulesCountSpan>
            <HealthCheckStatus $status={hasValidRules} />
          </RulesValidationContainer>
        </RulesTitleWithStatus>
        <RulesTextArea
          placeholder="Digite as premissas de neutralidade separadas por ponto e vírgula (;)&#10;Exemplo: Premissa 1; Premissa 2; Premissa 3"
          value={currentRules.neutralRules}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onRulesChange('neutralRules', e.target.value)}
        />
      </RulesSection>
    </PolarityContent>
  )
}
